import * as React from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { ButtonBase, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { mediaQuery } from '../../theme';
import { StatusChip } from '../ui';
import { aavOrderStatus } from '../../lib/aavOrderStatus';
import { formatAmount, formatDate } from '../../lib/formatDate';
import { useStyles } from './styles';

interface Props {
    orders: any[];
    paging: any;
    pagingUpdate: (params: any) => void;
}

const ROWS_PER_PAGE = [25, 50, 100, 500];

const detailsPath = (order: any) => `/aav-orders/${order.id}`;

const money = (order: any) => (order.totalPrice ? `${formatAmount(order.totalPrice)} ${order.currencyCode || ''}`.trim() : '—');

/** Row click is a mouse convenience only: it must not steal a text selection or a click on a link/button inside the row. */
const isPlainRowClick = (event: React.MouseEvent<HTMLElement>): boolean => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a, button, input, [role="button"]')) return false;
    return !(window.getSelection?.()?.toString());
};

/** AAV orders list: a dense table on desktop, one card per order under 768px. */
export default function AavOrdersTable({ orders, paging, pagingUpdate }: Props) {
    const classes = useStyles();
    const navigate = useNavigate();
    const theme = useTheme();
    const phone = useMediaQuery(mediaQuery(theme.custom.mq.sm));

    const handleChangePage = (_: unknown, newPage: number) => {
        pagingUpdate({ ...paging, page: newPage + 1 });
    };
    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        pagingUpdate({ ...paging, page: 1, limit: +e.target.value });
    };

    const pagination = (
        <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE}
            component="div"
            count={paging.total || 0}
            rowsPerPage={paging.limit || 50}
            page={Math.max(0, (paging.page || 1) - 1)}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={phone ? 'Rows' : 'Rows per page'}
        />
    );

    if (phone) {
        return (
            <>
                <div className={classes.cards}>
                    {orders.map((order) => {
                        const status = aavOrderStatus(order.statusId, order.status);
                        return (
                            <ButtonBase key={order.id} component="div" className={classes.card} onClick={() => navigate(detailsPath(order))}>
                                <div className={classes.cardTop}>
                                    <div>
                                        <div className={classes.orderLink}>#{order.orderNumber || order.id}</div>
                                        <div className={classes.orderEmail}>{order.email || order.customerEmail || '—'}</div>
                                    </div>
                                    <StatusChip tone={status.tone}>{status.label}</StatusChip>
                                </div>
                                <div className={classes.cardGrid}>
                                    <div><span className={classes.cardLabel}>Created</span><span className={classes.cardValue}>{formatDate(order.createdAt)}</span></div>
                                    <div><span className={classes.cardLabel}>Total</span><span className={classes.cardValue}>{money(order)}</span></div>
                                    <div><span className={classes.cardLabel}>Financial</span><span className={`${classes.cardValue} ${classes.capitalize}`}>{order.financialStatus || '—'}</span></div>
                                    <div><span className={classes.cardLabel}>Shop</span><span className={classes.cardValue}>{order.shopDomain || '—'}</span></div>
                                </div>
                            </ButtonBase>
                        );
                    })}
                </div>
                {pagination}
            </>
        );
    }

    return (
        <>
            <TableContainer className={classes.container}>
                <Table stickyHeader aria-label="AAV orders">
                    <TableHead>
                        <TableRow>
                            <TableCell>Order</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Financial</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell>Shop</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Updated</TableCell>
                            <TableCell padding="checkbox" aria-label="Open" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orders.map((order) => {
                            const status = aavOrderStatus(order.statusId, order.status);
                            const path = detailsPath(order);
                            return (
                                <TableRow
                                    hover
                                    key={order.id}
                                    className={classes.row}
                                    onClick={(event) => {
                                        if (isPlainRowClick(event)) navigate(path);
                                    }}
                                >
                                    <TableCell className={classes.orderCell}>
                                        <Link to={path} className={classes.orderLink}>#{order.orderNumber || order.id}</Link>
                                        <div className={classes.orderEmail}>{order.email || order.customerEmail || '—'}</div>
                                    </TableCell>
                                    <TableCell><StatusChip tone={status.tone}>{status.label}</StatusChip></TableCell>
                                    <TableCell className={classes.capitalize}>{order.financialStatus || '—'}</TableCell>
                                    <TableCell className={classes.numeric}>{money(order)}</TableCell>
                                    <TableCell className={classes.shop} title={order.shopDomain || ''}>{order.shopDomain || '—'}</TableCell>
                                    <TableCell className={classes.dateCell}>{formatDate(order.createdAt)}</TableCell>
                                    <TableCell className={classes.dateCell}>{formatDate(order.updatedAt)}</TableCell>
                                    <TableCell padding="checkbox">
                                        <Tooltip title="Open order">
                                            <IconButton
                                                size="small"
                                                className={classes.openButton}
                                                aria-label={`Open order ${order.orderNumber || order.id}`}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    navigate(path);
                                                }}
                                            >
                                                <ChevronRightIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {pagination}
        </>
    );
}
